import type { ReactNode } from 'react';
import { useId } from 'react';
import { cn } from './cn.js';
import { useRequiredMark } from './Form.js';
import { Icon, type IconName } from './Icon.js';

export interface ReadonlyFieldProps {
  label: string;
  /** O que mostrar. Vazio (`null`, `''`) mostra o `placeholder`. */
  value: ReactNode;
  help?: ReactNode;
  /** O campo conta para avançar — o `*` aparece, mesmo sem se digitar nele. */
  required?: boolean;
  error?: string | null;
  /** O que diz o vazio. */
  placeholder?: string;
  /** De onde o valor vem — `relogio` num prazo, por exemplo. Decorativo. */
  icon?: IconName;
  className?: string;
}

/**
 * O campo que só MOSTRA (15/10/2026): o valor calculado de um formulário — o
 * vencimento que a tentativa de contato grava, a contagem, a data em que o
 * card entrou na fase. Ninguém digita nele.
 *
 * ## Por que não é um `Field` com `readOnly`
 *
 * Porque ele parecia digitável: mesma caixa, mesma borda, mesmo cursor de
 * texto — e a pessoa clicava, tentava apagar, e não entendia por que nada
 * mudava. Aqui não há `<input>`: o valor é texto num bloco de fundo suave,
 * sem borda forte, com um cadeado que diz que ele não se edita. O rótulo,
 * o `*`, a ajuda e o erro seguem o `Field`, para o formulário continuar
 * alinhado.
 */
export function ReadonlyField({
  label,
  value,
  help,
  required,
  error,
  placeholder = 'Nada registrado ainda.',
  icon,
  className,
}: ReadonlyFieldProps) {
  const id = useId();
  const idLabel = `${id}-rotulo`;
  const idHelp = `${id}-ajuda`;
  const idError = `${id}-erro`;
  useRequiredMark(required);
  const empty = value === null || value === undefined || value === '';
  const described = [help ? idHelp : null, error ? idError : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <span id={idLabel} className="text-xs font-medium text-texto">
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </span>
      <div
        role="group"
        aria-labelledby={idLabel}
        aria-describedby={described || undefined}
        className={cn(
          'flex min-h-9 items-center gap-2 rounded-borda border bg-superficie-suave px-3 py-2 text-xs pointer-coarse:min-h-11',
          error ? 'border-erro' : 'border-borda',
        )}
      >
        {icon && <Icon nome={icon} size={16} className="shrink-0 text-texto-suave" />}
        <span
          className={cn(
            'min-w-0 flex-1 whitespace-pre-line',
            empty ? 'text-texto-tenue' : 'font-medium text-texto',
          )}
        >
          {empty ? placeholder : value}
        </span>
        <Icon
          nome="bloqueado"
          size={16}
          className="shrink-0 text-texto-tenue"
          label="Não se edita"
        />
      </div>
      {help && (
        <p id={idHelp} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={idError} className="flex items-center gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}

export type ReadonlyTextAreaProps = Omit<ReadonlyFieldProps, 'icon'>;

/**
 * O `ReadonlyField` para texto longo (15/10/2026): a mensagem gerada, o
 * parecer calculado. O mesmo fundo suave, o mesmo cadeado e o mesmo rótulo,
 * `*`, ajuda e erro — sem caixa de digitar. Quebra de linha e emoji do texto
 * ficam como estão, e o que passa da altura rola dentro do bloco — que recebe
 * foco pelo teclado, para quem não usa o mouse poder rolar.
 */
export function ReadonlyTextArea({
  label,
  value,
  help,
  required,
  error,
  placeholder = 'Nada registrado ainda.',
  className,
}: ReadonlyTextAreaProps) {
  const id = useId();
  const idLabel = `${id}-rotulo`;
  const idHelp = `${id}-ajuda`;
  const idError = `${id}-erro`;
  useRequiredMark(required);
  const empty = value === null || value === undefined || value === '';
  const described = [help ? idHelp : null, error ? idError : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <span id={idLabel} className="text-xs font-medium text-texto">
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-erro">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </span>
      <div
        role="group"
        aria-labelledby={idLabel}
        aria-describedby={described || undefined}
        tabIndex={0}
        className={cn(
          'relative min-h-40 max-h-96 overflow-y-auto rounded-borda border bg-superficie-suave py-2 pr-9 pl-3 text-xs leading-6',
          'focus-visible:outline-2 focus-visible:outline-foco',
          error ? 'border-erro' : 'border-borda',
        )}
      >
        <Icon
          nome="bloqueado"
          size={16}
          className="absolute top-2.5 right-2.5 text-texto-tenue"
          label="Não se edita"
        />
        <p
          className={cn(
            'break-words whitespace-pre-wrap',
            empty ? 'text-texto-tenue' : 'text-texto',
          )}
        >
          {empty ? placeholder : value}
        </p>
      </div>
      {help && (
        <p id={idHelp} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={idError} className="flex items-center gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}
